import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStorage, ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../../utils/cloudFunctions";
import {
  updateClone,
  calculateVoiceConfidence,
  confidenceLabel,
  generateSampleId,
  countWords,
  normalizeSampleText,
  EMAIL_TIERS,
  SUBJECT_TIERS,
  EMAIL_PRO_TARGET,
  SUBJECT_PRO_TARGET,
  getTier,
  tierHint,
  isCloneDnaOutdated,
  SUBJECT_CALIBRATION_CATEGORIES,
  autoCloneName,
  calibrationCoverage,
  subjectCalibrationCoverage,
  suggestSignOffName,
} from "./cloneUtils";
import { useCopyClone } from "./useCopyClones";
import { useDistillation } from "./useDistillation";
import VoiceDnaPreview from "./VoiceDnaPreview";
import CalibrationDeck from "./CalibrationDeck";
import SubjectCalibrationDeck from "./SubjectCalibrationDeck";
import NeuralCore from "./NeuralCore";

// id → label for subject tactics, used to render subjectDNA.psychPatterns.
const SUBJECT_TACTIC_LABELS = SUBJECT_CALIBRATION_CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.label;
  return acc;
}, {});

const MIN_SAMPLES_FOR_TRAINING = 2;

const TONES = [
  { id: "casual", label: "Casual", icon: "fa-face-smile" },
  { id: "formal", label: "Formal", icon: "fa-handshake" },
  { id: "mixed", label: "Mixed", icon: "fa-shuffle" },
];

// Sub-navigation for the Setup view. The dashboard is the home base (Neural
// Core + open to-dos); the rest are the workflow steps ordered by what matters
// most. `needsDna` items stay locked until the clone has been trained;
// `optional` items never block and are only suggested once trained.
const SUBNAV = [
  { id: "dashboard",    label: "Dashboard",          hint: "Overview & training",          icon: "fa-gauge-high" },
  { id: "samples",      label: "Add samples",        hint: "Feed emails & subjects",       icon: "fa-inbox" },
  { id: "calibration",  label: "Calibrate voice",    hint: "Swipe-verify the result",      icon: "fa-check-double", needsDna: true },
  { id: "subjectcalib", label: "Calibrate subjects", hint: "Tune subject-line style",      icon: "fa-heading",      needsDna: true },
  { id: "profile",      label: "Fine-tune voice",    hint: "Tone, style hint, background",  icon: "fa-fingerprint", optional: true },
  { id: "guardrails",   label: "Guardrails",         hint: "Taboos & sign-off rules",      icon: "fa-shield-halved", optional: true },
];

// Debounced auto-save — feels like Notion, no "Save" button anywhere.
// `markClean` flips the status indicator back to "Synced" once the write
// lands. We keep a ref to the latest draft so a save that finished while the
// user kept typing does NOT mark the editor clean (the newer edit is still
// pending its own debounced write).
const useAutoSave = (userId, cloneId, draft, isDirty, markClean) => {
  const timer = useRef(null);
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  useEffect(() => {
    if (!isDirty || !userId || !cloneId) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const snapshot = latestDraft.current;
      try {
        // CRITICAL: never let the editor's auto-save persist `calibration` /
        // `subjectCalibration`. Those are written out-of-band by the
        // CalibrationDeck (direct RTDB writes) and are NOT re-hydrated into the
        // editor draft, so the draft copy is stale. Writing it back here would
        // clobber a freshly completed calibration with the empty default the
        // clone was created with — exactly the "not calibrated yet" data loss.
        const persisted = { ...snapshot };
        delete persisted.calibration;
        delete persisted.subjectCalibration;
        await updateClone(userId, cloneId, persisted);
      } finally {
        // Only settle to "Synced" if no newer keystroke replaced the draft
        // while the write was in flight.
        if (latestDraft.current === snapshot) markClean();
      }
    }, 600);
    return () => clearTimeout(timer.current);
  }, [draft, isDirty, userId, cloneId, markClean]);
};

// ---------------- LEFT PANEL SECTIONS ----------------

const IdentitySection = ({ draft, patch }) => {
  // Tone + voice description are auto-handled (tone defaults, description can be
  // one-tap filled from the learned voice), so they're tucked behind a toggle.
  // The Profile label now lives in the editor title — no field here anymore.
  const [styleOpen, setStyleOpen] = useState(false);

  const story = draft.identity?.story || "";
  const tone = draft.identity?.tone || "casual";
  const dnaSummary = (draft.voiceDNA?.voiceFingerprint || "").trim();
  // Trim the learned essence to ~2 sentences for a usable description suggestion.
  const suggestion = dnaSummary
    ? dnaSummary.split(/(?<=[.!?])\s+/).slice(0, 2).join(" ").trim()
    : "";
  const toneLabel = TONES.find((t) => t.id === tone)?.label || "Casual";

  return (
    <div className="cca-section">
      <h3 className="cca-section-title">Voice profile</h3>
      <p className="cca-section-hint">
        Defines <strong>how</strong> this CopyTwin writes, learned automatically
        from your samples. Rename it in the title above; fine-tune the details
        below only if you want.
      </p>

      <button
        type="button"
        className="cca-style-toggle"
        aria-expanded={styleOpen}
        onClick={() => setStyleOpen((v) => !v)}
      >
        <span className="cca-style-toggle-main">
          <i className="fa-solid fa-sliders" />
          Tone &amp; style hint
        </span>
        <span className="cca-style-toggle-meta">
          {toneLabel}
          {story ? " · custom hint" : suggestion ? " · auto" : ""}
          <i
            className={`fas fa-chevron-down cca-style-chevron ${
              styleOpen ? "cca-style-chevron--open" : ""
            }`}
          />
        </span>
      </button>

      {styleOpen && (
        <div className="cca-style-body">
          <div className="cca-field">
            <span className="cca-field-label">Default tone</span>
            <div className="cca-tone-row">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`cca-tone-btn ${tone === t.id ? "active" : ""}`}
                  onClick={() =>
                    patch({ identity: { ...draft.identity, tone: t.id } })
                  }
                >
                  <i className={`fa-solid ${t.icon}`} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <label className="cca-field">
            <span className="cca-field-label">
              Voice description
              <span className="cca-field-label-hint">A style hint for the AI, never used as facts.</span>
            </span>
            <textarea
              className="cca-textarea"
              rows={2}
              value={story}
              placeholder={
                suggestion ||
                "Sharp, no-fluff. Writes like a smart friend who gets to the point in two lines."
              }
              onChange={(e) =>
                patch({ identity: { ...draft.identity, story: e.target.value } })
              }
            />
            {!story && suggestion && (
              <button
                type="button"
                className="cca-style-autofill"
                onClick={() =>
                  patch({ identity: { ...draft.identity, story: suggestion } })
                }
              >
                <i className="fa-solid fa-wand-magic-sparkles" />
                Use what we learned from your voice
              </button>
            )}
          </label>
        </div>
      )}

      <WriterBackgroundField draft={draft} patch={patch} />
    </div>
  );
};

// Writer background — auto-inferred "who is this writer / what's their world"
// context (voiceDNA.voiceContext), shown read-only with a toggle that controls
// whether it's fed to the generator at send time. Always SUBORDINATE to the
// campaign brief; the toggle lets the user kill it if it ever causes drift.
const WriterBackgroundField = ({ draft, patch }) => {
  const vc = draft.voiceDNA?.voiceContext;
  const enabled = draft.voiceContextEnabled !== false; // default ON
  const hasContent =
    vc &&
    (vc.persona || vc.expertise || vc.offering || vc.valueProposition ||
      (Array.isArray(vc.topics) && vc.topics.length > 0));

  // The inferred background is several long paragraphs, so we fold it behind a
  // toggle by default and let the user expand the full detail on demand.
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="cca-field">
      <div className="cca-wbg-head">
        <span className="cca-field-label" style={{ marginBottom: 0 }}>
          Writer background
          <span className="cca-field-label-hint">
            Auto-learned from your emails. Soft context only. The campaign brief
            always wins.
          </span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          className={`cca-toggle ${enabled ? "on" : ""}`}
          onClick={() => patch({ voiceContextEnabled: !enabled })}
          title={enabled ? "Used when writing. Click to disable" : "Ignored when writing. Click to enable"}
        >
          <span className="cca-toggle-knob" />
        </button>
      </div>

      {hasContent ? (
        <>
          <button
            type="button"
            className="cca-wbg-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <span>{expanded ? "Hide background details" : "Show background details"}</span>
            <i
              className={`fas fa-chevron-down cca-wbg-chevron ${
                expanded ? "cca-wbg-chevron--open" : ""
              }`}
            />
          </button>
          {expanded && (
        <div className={`cca-wbg-card ${enabled ? "" : "muted"}`}>
          {vc.persona && (
            <p className="cca-wbg-row"><strong>Who:</strong> {vc.persona}</p>
          )}
          {vc.expertise && (
            <p className="cca-wbg-row"><strong>Expertise:</strong> {vc.expertise}</p>
          )}
          {vc.offering && (
            <p className="cca-wbg-row"><strong>Offers:</strong> {vc.offering}</p>
          )}
          {vc.valueProposition && (
            <p className="cca-wbg-row"><strong>Value:</strong> {vc.valueProposition}</p>
          )}
          {Array.isArray(vc.topics) && vc.topics.length > 0 && (
            <div className="cca-wbg-topics">
              {vc.topics.map((t, i) => (
                <span key={i} className="cca-wbg-topic">{t}</span>
              ))}
            </div>
          )}
        </div>
          )}
        </>
      ) : (
        <div className="cca-wbg-pending">
          <i className="fa-solid fa-circle-info" />
          Train the CopyTwin and the inferred background will appear here.
        </div>
      )}
    </div>
  );
};

// Color/icon mapping for structural beats — used to render the annotation
// trace produced by the distillation engine.
const BEAT_META = {
  greeting:  { label: "Greeting",  icon: "fa-hand",          accent: "blue"   },
  opener:    { label: "Opener",    icon: "fa-bolt",          accent: "purple" },
  context:   { label: "Context",   icon: "fa-circle-info",   accent: "blue"   },
  value:     { label: "Value",     icon: "fa-gem",           accent: "green"  },
  evidence:  { label: "Evidence",  icon: "fa-chart-simple",  accent: "amber"  },
  cta:       { label: "CTA",       icon: "fa-bullseye",      accent: "purple" },
  sign_off:  { label: "Sign-off",  icon: "fa-pen-nib",       accent: "blue"   },
};

const BeatRow = ({ beat }) => {
  const meta = BEAT_META[beat.beat] || {
    label: beat.beat,
    icon: "fa-circle",
    accent: "default",
  };
  return (
    <div className="cca-beat-row" data-accent={meta.accent}>
      <div className="cca-beat-head">
        <i className={`fa-solid ${meta.icon}`} />
        <span className="cca-beat-label">{meta.label}</span>
      </div>
      <div className="cca-beat-text">{beat.text}</div>
      {beat.techniques?.length > 0 && (
        <div className="cca-beat-techniques">
          {beat.techniques.map((t, i) => (
            <span key={i} className="cca-beat-technique">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const EmailAnnotation = ({ annotation }) => (
  <div className="cca-annotation">
    <div className="cca-annotation-header">
      <i className="fa-solid fa-fingerprint" />
      <span>Detected by Voice DNA</span>
    </div>
    <div className="cca-annotation-meta">
      {annotation.dominantTone && (
        <span className="cca-annotation-pill">
          <strong>Tone:</strong> {annotation.dominantTone}
        </span>
      )}
      {annotation.inferredAudience && (
        <span className="cca-annotation-pill">
          <strong>Audience:</strong> {annotation.inferredAudience}
        </span>
      )}
      {annotation.inferredGoal && (
        <span className="cca-annotation-pill">
          <strong>Goal:</strong> {annotation.inferredGoal}
        </span>
      )}
    </div>
    {annotation.beats?.length > 0 && (
      <div className="cca-beats">
        {annotation.beats.map((b, i) => (
          <BeatRow key={i} beat={b} />
        ))}
      </div>
    )}
    {annotation.summary && (
      <div className="cca-annotation-summary">
        <i className="fa-solid fa-quote-left" />
        <span>{annotation.summary}</span>
      </div>
    )}
  </div>
);

const ACCEPTED_DOC_EXT = /\.(pdf|docx)$/i;
const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10 MB — matches the backend

const EmailLibrarySection = ({ draft, patch, userId }) => {
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ subject: "", body: "" });

  // Document import (PDF / DOCX → AI split into individual emails)
  const fileInputRef = useRef(null);
  const [importState, setImportState] = useState("idle"); // idle | uploading | extracting | review | error
  const [importError, setImportError] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [detected, setDetected] = useState([]); // [{ subject, body, include }]

  const fullEmails = draft.voiceSamples?.fullEmails || [];
  const subjectLines = draft.voiceSamples?.subjectLines || [];

  const resetImport = () => {
    setImportState("idle");
    setImportError("");
    setImportFileName("");
    setDetected([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_DOC_EXT.test(file.name)) {
      setImportState("error");
      setImportError("Only PDF and DOCX files are supported.");
      return;
    }
    if (file.size > MAX_DOC_SIZE) {
      setImportState("error");
      setImportError("File too large (max 10 MB).");
      return;
    }
    if (!userId) {
      setImportState("error");
      setImportError("Not signed in — cannot upload.");
      return;
    }

    setImportError("");
    setImportFileName(file.name);
    setImportState("uploading");

    let storagePath = null;
    try {
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      storagePath = `copyclone_imports/${userId}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(storage, storagePath);
      await uploadBytes(fileRef, file);

      setImportState("extracting");
      const response = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.extractEmailsFromDocument(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            storagePath,
            fileName: file.name,
            mimeType: file.type,
            userId,
          }),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Import failed");
      }

      const result = await response.json();
      const emails = (result.emails || []).map((em) => ({
        subject: em.subject || "",
        body: em.body || "",
        include: true,
      }));

      if (!emails.length) {
        setImportState("error");
        setImportError("No emails detected in this document.");
        return;
      }

      setDetected(emails);
      setImportState("review");
    } catch (err) {
      console.error("[CopyClone import] failed:", err);
      setImportState("error");
      setImportError(err.message || "Import failed");
    } finally {
      // The extracted text already lives in our result — the raw upload is
      // no longer needed, so clean it up best-effort.
      if (storagePath) {
        try {
          await deleteObject(storageRef(getStorage(), storagePath));
        } catch (_) {
          /* non-fatal */
        }
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleDetected = (idx) =>
    setDetected((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, include: !d.include } : d)),
    );

  const updateDetectedSubject = (idx, value) =>
    setDetected((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, subject: value } : d)),
    );

  const confirmImport = () => {
    const chosen = detected.filter((d) => d.include && d.body.trim());
    if (!chosen.length) {
      resetImport();
      return;
    }
    const additions = chosen.map((d) => {
      const cleanBody = normalizeSampleText(d.body);
      return {
        id: generateSampleId(),
        subject: (d.subject || "").trim(),
        body: cleanBody,
        wordCount: countWords(cleanBody),
        addedAt: Date.now(),
      };
    });

    // The detected subject lines should also feed the dedicated subject-line
    // library so the dashboard reflects them. Dedupe against existing lines
    // and within the import itself (case-insensitive).
    const seenSubjects = new Set(
      subjectLines.map((s) => (s.text || "").trim().toLowerCase()),
    );
    const subjectAdditions = [];
    for (const a of additions) {
      const text = a.subject;
      if (!text) continue;
      const key = text.toLowerCase();
      if (seenSubjects.has(key)) continue;
      seenSubjects.add(key);
      subjectAdditions.push({
        id: generateSampleId(),
        text,
        addedAt: Date.now(),
      });
    }

    patch({
      voiceSamples: {
        ...draft.voiceSamples,
        fullEmails: [...fullEmails, ...additions],
        subjectLines: [...subjectLines, ...subjectAdditions],
      },
    });
    resetImport();
  };

  const selectedCount = detected.filter((d) => d.include).length;

  const addEmail = () => {
    const cleanBody = normalizeSampleText(emailDraft.body);
    if (!cleanBody) return;
    const subject = emailDraft.subject.trim();
    const next = [
      ...fullEmails,
      {
        id: generateSampleId(),
        subject,
        body: cleanBody,
        wordCount: countWords(cleanBody),
        addedAt: Date.now(),
      },
    ];

    // Carry the subject into the dedicated subject-line library too (deduped),
    // so it counts toward subject-line training.
    let nextSubjects = subjectLines;
    if (subject) {
      const exists = subjectLines.some(
        (s) => (s.text || "").trim().toLowerCase() === subject.toLowerCase(),
      );
      if (!exists) {
        nextSubjects = [
          ...subjectLines,
          { id: generateSampleId(), text: subject, addedAt: Date.now() },
        ];
      }
    }

    patch({ voiceSamples: { ...draft.voiceSamples, fullEmails: next, subjectLines: nextSubjects } });
    setEmailDraft({ subject: "", body: "" });
  };

  const removeEmail = (id) => {
    if (editingId === id) cancelEdit();
    patch({
      voiceSamples: {
        ...draft.voiceSamples,
        fullEmails: fullEmails.filter((e) => e.id !== id),
        subjectLines,
      },
    });
  };

  const startEdit = (email) => {
    setEditingId(email.id);
    setEditDraft({ subject: email.subject || "", body: email.body || "" });
    setExpandedId(email.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ subject: "", body: "" });
  };

  const saveEdit = (id) => {
    const cleanBody = normalizeSampleText(editDraft.body);
    if (!cleanBody) return;
    patch({
      voiceSamples: {
        ...draft.voiceSamples,
        fullEmails: fullEmails.map((e) =>
          e.id === id
            ? {
                ...e,
                subject: editDraft.subject.trim(),
                body: cleanBody,
                wordCount: countWords(cleanBody),
              }
            : e,
        ),
        subjectLines,
      },
    });
    setEditingId(null);
    setEditDraft({ subject: "", body: "" });
  };

  return (
    <div className="cca-section">
      <h3 className="cca-section-title">Email library</h3>
      <p className="cca-section-hint">
        Paste real emails you have sent. The Voice DNA engine will dissect each
        one into structural beats (greeting, opener, value, CTA, sign-off) and
        learn the techniques you use for each. <strong>Style is captured,
        content stays per-contact.</strong>
      </p>

      <div className="cca-lib-cols">
        <div className="cca-lib-col cca-lib-col--compose">

      {/* === Bulk import: upload a PDF / DOCX with many emails === */}
      <div className="cca-import">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: "none" }}
          onChange={handleFilePicked}
        />

        {(importState === "idle" || importState === "error") && (
          <>
            <button
              type="button"
              className="cca-import-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="fa-solid fa-file-arrow-up" />
              Import from PDF / DOCX
            </button>
            <span className="cca-import-hint">
              Upload a document with several emails (e.g. a Google&nbsp;Doc
              export) and the AI splits it into individual emails &amp; subjects
              for you to review.
            </span>
            {importState === "error" && (
              <span className="cca-import-error">
                <i className="fa-solid fa-triangle-exclamation" /> {importError}
              </span>
            )}
          </>
        )}

        {(importState === "uploading" || importState === "extracting") && (
          <div className="cca-import-loading">
            <i className="fa-solid fa-spinner fa-spin" />
            <span>
              {importState === "uploading"
                ? `Uploading ${importFileName}…`
                : "AI is reading the document and splitting it into emails…"}
            </span>
          </div>
        )}

        {importState === "review" && (
          <div className="cca-import-review">
            <div className="cca-import-review-head">
              <span className="cca-import-review-title">
                <i className="fa-solid fa-wand-magic-sparkles" />
                Found {detected.length} email
                {detected.length === 1 ? "" : "s"} in{" "}
                <strong>{importFileName}</strong>
              </span>
              <span className="cca-import-review-sub">
                Review &amp; deselect anything that isn&apos;t a real email,
                then add the rest to your library.
              </span>
            </div>

            <div className="cca-import-review-list">
              {detected.map((d, idx) => (
                <div
                  key={idx}
                  className={`cca-import-card ${d.include ? "on" : "off"}`}
                >
                  <label className="cca-import-card-head">
                    <input
                      type="checkbox"
                      checked={d.include}
                      onChange={() => toggleDetected(idx)}
                    />
                    <input
                      type="text"
                      className="cca-import-card-subject"
                      value={d.subject}
                      placeholder="(no subject)"
                      onChange={(e) =>
                        updateDetectedSubject(idx, e.target.value)
                      }
                    />
                    <span className="cca-import-card-words">
                      {countWords(d.body)}w
                    </span>
                  </label>
                  <pre className="cca-import-card-body">{d.body}</pre>
                </div>
              ))}
            </div>

            <div className="cca-import-review-actions">
              <button
                type="button"
                className="cca-import-cancel"
                onClick={resetImport}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cca-btn-primary"
                onClick={confirmImport}
                disabled={selectedCount === 0}
              >
                <i className="fa-solid fa-plus" />
                Add {selectedCount} to library
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="cca-mc">
        <div className="cca-mc-header">
          <span className="cca-mc-label">Subject:</span>
          <input
            type="text"
            className="cca-mc-subject"
            value={emailDraft.subject}
            placeholder="Add a subject (optional, but improves training)"
            onChange={(e) =>
              setEmailDraft({ ...emailDraft, subject: e.target.value })
            }
          />
          <span className="cca-mc-counter" title="Emails in library">
            {fullEmails.length}
          </span>
        </div>

        <div className="cca-mc-divider" />

        <textarea
          className="cca-mc-body"
          rows={9}
          value={emailDraft.body}
          placeholder={
            "Paste a real email you have sent. Use [Shopname], [firstName], [city] for the parts that change per recipient. Only the writing style matters.\n\n" +
            "The more variety you add (intros, follow-ups, replies), the smarter your CopyTwin."
          }
          onChange={(e) =>
            setEmailDraft({ ...emailDraft, body: e.target.value })
          }
        />

        <div className="cca-mc-footer">
          <span className="cca-mc-words">
            <i className="fa-regular fa-keyboard" />
            {countWords(emailDraft.body)} words
          </span>
          <button
            type="button"
            className="cca-mc-add"
            onClick={addEmail}
            disabled={!emailDraft.body.trim()}
          >
            <i className="fa-solid fa-plus" />
            Add to library
          </button>
        </div>
      </div>

        </div>

        <div className="cca-lib-col cca-lib-col--list">
          <div className="cca-lib-list-head">
            <span className="cca-lib-list-title">
              <i className="fa-regular fa-rectangle-list" /> Your emails
            </span>
            <span className="cca-lib-list-count">{fullEmails.length}</span>
          </div>

          {fullEmails.length === 0 ? (
            <div className="cca-lib-list-empty">
              <i className="fa-regular fa-envelope-open" />
              <span>
                No emails yet. Add a sample on the left and it will show up here
                to expand &amp; edit.
              </span>
            </div>
          ) : (
            <div className="cca-email-list">
              {fullEmails.map((e) => {
                const isOpen = expandedId === e.id;
                const isEditing = editingId === e.id;
                const hasAnnotation = !!e.annotation;
                return (
                  <div
                    key={e.id}
                    className={`cca-email-item ${hasAnnotation ? "annotated" : ""} ${
                      isEditing ? "editing" : ""
                    }`}
                  >
                    <div className="cca-email-item-head">
                      <button
                        type="button"
                        className="cca-email-item-head-btn"
                        onClick={() => setExpandedId(isOpen ? null : e.id)}
                      >
                        <span className="cca-email-item-chevron">
                          <i className={`fa-solid ${isOpen ? "fa-chevron-down" : "fa-chevron-right"}`} />
                        </span>
                        <span className="cca-email-item-subject">
                          {e.subject || "(no subject)"}
                        </span>
                        <span className="cca-email-item-meta">
                          {hasAnnotation && (
                            <span className="cca-email-item-status">
                              <i className="fa-solid fa-fingerprint" />
                              analyzed
                            </span>
                          )}
                          <span className="cca-email-item-words">{e.wordCount}w</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="cca-email-item-edit"
                        onClick={() => (isEditing ? cancelEdit() : startEdit(e))}
                        title={isEditing ? "Cancel editing" : "Edit sample"}
                      >
                        <i className={`fa-solid ${isEditing ? "fa-xmark" : "fa-pen"}`} />
                      </button>
                      <button
                        type="button"
                        className="cca-email-item-del"
                        onClick={() => removeEmail(e.id)}
                        title="Remove sample"
                      >
                        <i className="fa-regular fa-trash-can" />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="cca-email-item-body-full">
                        {isEditing ? (
                          <div className="cca-email-edit">
                            <input
                              type="text"
                              className="cca-email-edit-subject"
                              value={editDraft.subject}
                              placeholder="Subject (optional)"
                              onChange={(ev) =>
                                setEditDraft({ ...editDraft, subject: ev.target.value })
                              }
                            />
                            <textarea
                              className="cca-email-edit-body"
                              rows={8}
                              value={editDraft.body}
                              placeholder="Email body…"
                              onChange={(ev) =>
                                setEditDraft({ ...editDraft, body: ev.target.value })
                              }
                            />
                            <div className="cca-email-edit-actions">
                              <span className="cca-email-edit-words">
                                <i className="fa-regular fa-keyboard" />
                                {countWords(editDraft.body)} words
                              </span>
                              <div className="cca-email-edit-buttons">
                                <button
                                  type="button"
                                  className="cca-email-edit-cancel"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="cca-email-edit-save"
                                  onClick={() => saveEdit(e.id)}
                                  disabled={!editDraft.body.trim()}
                                >
                                  <i className="fa-solid fa-check" />
                                  Save changes
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <pre>{e.body}</pre>
                            {hasAnnotation && <EmailAnnotation annotation={e.annotation} />}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SubjectLinesSection = ({ draft, patch }) => {
  const [subjectDraft, setSubjectDraft] = useState("");

  const subjectLines = draft.voiceSamples?.subjectLines || [];
  const fullEmails = draft.voiceSamples?.fullEmails || [];

  const addSubject = () => {
    const text = subjectDraft.trim();
    if (!text) return;
    const next = [
      ...subjectLines,
      { id: generateSampleId(), text, addedAt: Date.now() },
    ];
    patch({ voiceSamples: { ...draft.voiceSamples, subjectLines: next, fullEmails } });
    setSubjectDraft("");
  };

  // Bulk paste: accept newline-separated lines
  const handlePaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    if (!pasted.includes("\n")) return; // single line — let normal flow handle it
    e.preventDefault();
    const lines = pasted
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    const additions = lines.map((text) => ({
      id: generateSampleId(),
      text,
      addedAt: Date.now(),
    }));
    patch({
      voiceSamples: {
        ...draft.voiceSamples,
        subjectLines: [...subjectLines, ...additions],
        fullEmails,
      },
    });
    setSubjectDraft("");
  };

  const removeSubject = (id) => {
    patch({
      voiceSamples: {
        ...draft.voiceSamples,
        subjectLines: subjectLines.filter((s) => s.id !== id),
        fullEmails,
      },
    });
  };

  return (
    <div className="cca-section">
      <h3 className="cca-section-title">Subject lines</h3>
      <p className="cca-section-hint">
        Subject lines have their own DNA: length, capitalization, hook style,
        punctuation. Paste a bunch (one per line works too).
      </p>

      <div className="cca-sub">
        <div className="cca-sub-header">
          <h4 className="cca-sub-title">Add subject lines</h4>
          <span className="cca-sub-count">{subjectLines.length}</span>
        </div>

        <div className="cca-snippet-input-row">
          <input
            type="text"
            className="cca-input"
            value={subjectDraft}
            placeholder="A subject you would write…"
            onChange={(e) => setSubjectDraft(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === "Enter") addSubject();
            }}
          />
          <button
            type="button"
            className="cca-btn-primary"
            onClick={addSubject}
            disabled={!subjectDraft.trim()}
          >
            Add
          </button>
        </div>

        {subjectLines.length > 0 && (
          <div className="cca-subject-grid">
            {subjectLines.map((s) => (
              <div key={s.id} className="cca-subject-chip">
                <span className="cca-subject-chip-text">{s.text}</span>
                <button
                  type="button"
                  className="cca-subject-chip-del"
                  onClick={() => removeSubject(s.id)}
                  title="Remove"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {draft.subjectDNA && (
        <div className="cca-sub cca-sub-dna">
          <div className="cca-sub-header">
            <h4 className="cca-sub-title">
              <i className="fa-solid fa-fingerprint" /> Subject DNA
            </h4>
          </div>
          {draft.subjectDNA.fingerprint && (
            <p className="cca-vdna-prose">{draft.subjectDNA.fingerprint}</p>
          )}
          <div className="cca-subject-dna-stats">
            {draft.subjectDNA.avgWords != null && (
              <div className="cca-subject-dna-stat">
                <span className="cca-subject-dna-stat-value">
                  {draft.subjectDNA.avgWords.toFixed(1)}
                </span>
                <span className="cca-subject-dna-stat-label">avg words</span>
              </div>
            )}
            {draft.subjectDNA.capitalization && (
              <div className="cca-subject-dna-stat">
                <span className="cca-subject-dna-stat-value cca-subject-dna-stat-text">
                  {draft.subjectDNA.capitalization}
                </span>
                <span className="cca-subject-dna-stat-label">capitalization</span>
              </div>
            )}
            {draft.subjectDNA.punctuation && (
              <div className="cca-subject-dna-stat">
                <span className="cca-subject-dna-stat-value cca-subject-dna-stat-text">
                  {draft.subjectDNA.punctuation}
                </span>
                <span className="cca-subject-dna-stat-label">punctuation</span>
              </div>
            )}
          </div>
          {draft.subjectDNA.patterns?.length > 0 && (
            <div className="cca-vdna-chips cca-vdna-chips-purple">
              {draft.subjectDNA.patterns.map((p, i) => (
                <span key={i} className="cca-vdna-chip">{p}</span>
              ))}
            </div>
          )}
          {draft.subjectDNA.psychPatterns?.length > 0 && (
            <div className="cca-subject-dna-tactics">
              <div className="cca-subject-dna-tactics-label">
                <i className="fa-solid fa-bullseye" /> Tactics learned
              </div>
              <div className="cca-vdna-chips cca-vdna-chips-purple">
                {draft.subjectDNA.psychPatterns.map((p, i) => (
                  <span key={i} className="cca-vdna-chip">
                    {SUBJECT_TACTIC_LABELS[p.tactic] || p.tactic}
                    {p.count > 1 ? ` ×${p.count}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          {draft.subjectDNA.examples?.length > 0 && (
            <div className="cca-subject-dna-examples">
              <div className="cca-subject-dna-examples-label">
                <i className="fa-solid fa-quote-left" /> Your subject lines the
                AI copies the style of
              </div>
              <ul className="cca-subject-dna-examples-list">
                {draft.subjectDNA.examples.map((ex, i) => (
                  <li key={i}>"{ex}"</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ChipList = ({ items, onAdd, onRemove, placeholder, accent }) => {
  const [val, setVal] = useState("");
  return (
    <div className={`cca-chips cca-chips-${accent}`}>
      {(items || []).map((item, idx) => (
        <span key={idx} className="cca-chip">
          {item}
          <button type="button" onClick={() => onRemove(idx)}>
            <i className="fa-solid fa-xmark" />
          </button>
        </span>
      ))}
      <input
        className="cca-chip-input"
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && val.trim()) {
            onAdd(val.trim());
            setVal("");
          }
        }}
      />
    </div>
  );
};

// Curated quick-add suggestions — universal AI-tells across EN + DE that
// almost every user wants blocked. Clicking adds them to the clone's taboos.
const TABOO_SUGGESTIONS = [
  { label: "— (em dashes)",            value: "em dashes (—)"                     },
  { label: '"I hope this finds you well"', value: '"I hope this email finds you well"' },
  { label: '"delve"',                  value: '"delve"'                          },
  { label: '"leverage"',               value: '"leverage"'                       },
  { label: '"synergy"',                value: '"synergy"'                        },
  { label: '"furthermore" / "moreover"', value: '"furthermore", "moreover"'      },
  { label: '"Best regards"',           value: '"Best regards"'                   },
  { label: '"Mit freundlichen Grüßen"',value: '"Mit freundlichen Grüßen"'        },
  { label: '"Ich hoffe es geht dir gut"', value: '"Ich hoffe es geht dir gut"'   },
  { label: '"darüber hinaus"',         value: '"darüber hinaus"'                 },
  { label: "AI-perfect punctuation",   value: "AI-style perfect punctuation, no typos, no slang" },
];

const LANGUAGES = [
  { id: "auto", label: "Auto from samples", icon: "fa-wand-magic-sparkles" },
  { id: "en",   label: "English",           icon: "fa-flag" },
  { id: "de",   label: "German",            icon: "fa-flag" },
  { id: "fr",   label: "French",            icon: "fa-flag" },
  { id: "es",   label: "Spanish",           icon: "fa-flag" },
  { id: "it",   label: "Italian",           icon: "fa-flag" },
  { id: "nl",   label: "Dutch",             icon: "fa-flag" },
  { id: "pt",   label: "Portuguese",        icon: "fa-flag" },
];

const GuardrailsSection = ({ draft, patch }) => {
  const taboos = draft.taboos || [];
  const signOffMode = draft.signOff?.mode || "auto";
  const detectedClosings = draft.voiceDNA?.closingPatterns || [];
  const language = draft.language || "auto";

  const signOffName = draft.signOff?.name || "";
  const suggestedName = useMemo(
    () => suggestSignOffName(draft),
    // Suggestion only depends on the detected closing patterns, not keystrokes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.voiceDNA?.closingPatterns],
  );
  const setSignOffName = (value) =>
    patch({ signOff: { ...draft.signOff, name: value } });

  const addTaboo = (value) => {
    if (!value || !value.trim()) return;
    if (taboos.some((t) => t.toLowerCase() === value.toLowerCase())) return;
    patch({ taboos: [...taboos, value] });
  };

  const availableSuggestions = TABOO_SUGGESTIONS.filter(
    (s) => !taboos.some((t) => t.toLowerCase() === s.value.toLowerCase()),
  );

  return (
    <div className="cca-section">
      <h3 className="cca-section-title">Style guardrails</h3>
      <p className="cca-section-hint">
        Constraints on <strong>how</strong> the CopyTwin writes, not <strong>what</strong> it says.
      </p>

      <div className="cca-architecture-note">
        <div className="cca-architecture-note-icon">
          <i className="fa-solid fa-circle-info" />
        </div>
        <div className="cca-architecture-note-body">
          <div className="cca-architecture-note-title">
            Style lives here. Content lives in templates.
          </div>
          <div className="cca-architecture-note-text">
            Your CopyTwin learns your <strong>voice</strong>: rhythm, vocabulary,
            structure, taboo phrases. <br />
            Facts, proof points, angles and trust anchors are added per-campaign
            inside the email templates that select this twin. One voice → many
            campaigns.
          </div>
        </div>
      </div>

      {/* SIGN-OFF — toggle between auto-derived and fixed string */}
      <div className="cca-guard">
        <div className="cca-guard-header">
          <i className="fa-solid fa-pen-nib" style={{ color: "#6897B7" }} />
          <div>
            <div className="cca-guard-title">Sign-off behavior</div>
            <div className="cca-guard-hint">
              How this CopyTwin closes its emails.
            </div>
          </div>
        </div>

        <div className="cca-signoff-toggle">
          <button
            type="button"
            className={`cca-signoff-opt ${signOffMode === "auto" ? "active" : ""}`}
            onClick={() =>
              patch({ signOff: { ...draft.signOff, mode: "auto" } })
            }
          >
            <span className="cca-signoff-opt-icon">
              <i className="fa-solid fa-wand-magic-sparkles" />
            </span>
            <span className="cca-signoff-opt-body">
              <span className="cca-signoff-opt-title">Auto from emails</span>
              <span className="cca-signoff-opt-hint">
                Voice DNA learns the sign-off pattern from your samples.
              </span>
            </span>
            {signOffMode === "auto" && (
              <span className="cca-signoff-opt-check">
                <i className="fa-solid fa-check" />
              </span>
            )}
          </button>

          <button
            type="button"
            className={`cca-signoff-opt ${signOffMode === "fixed" ? "active" : ""}`}
            onClick={() =>
              patch({ signOff: { ...draft.signOff, mode: "fixed" } })
            }
          >
            <span className="cca-signoff-opt-icon">
              <i className="fa-solid fa-thumbtack" />
            </span>
            <span className="cca-signoff-opt-body">
              <span className="cca-signoff-opt-title">Always the same</span>
              <span className="cca-signoff-opt-hint">
                Pin a fixed sign-off used in every generated email.
              </span>
            </span>
            {signOffMode === "fixed" && (
              <span className="cca-signoff-opt-check">
                <i className="fa-solid fa-check" />
              </span>
            )}
          </button>
        </div>

        {/* SIGNATURE NAME — stored once, reused whenever a template's sign-off
            identity is "active sender" but no sender name is configured.
            Without this the closer is dropped entirely. */}
        <div className="cca-signoff-name">
          <label className="cca-signoff-name-label" htmlFor="cca-signoff-name-input">
            <i className="fa-solid fa-signature" />
            Signature name
          </label>
          <input
            id="cca-signoff-name-input"
            type="text"
            className="cca-input cca-signoff-name-input"
            value={signOffName}
            placeholder={suggestedName || "e.g. Jan"}
            onChange={(e) => setSignOffName(e.target.value)}
          />
          <div className="cca-signoff-name-hint">
            The name this CopyTwin signs with. Reused automatically when a
            template uses the active sender but no sender name is set — so the
            closer keeps the name instead of dropping it. Leave empty to keep
            the old behavior. A different name on a template still overrides this.
          </div>
          {suggestedName && suggestedName !== signOffName.trim() && (
            <button
              type="button"
              className="cca-signoff-name-suggest"
              onClick={() => setSignOffName(suggestedName)}
            >
              <i className="fa-solid fa-wand-magic-sparkles" />
              Use detected name: <strong>{suggestedName}</strong>
            </button>
          )}
        </div>

        {signOffMode === "fixed" && (
          <textarea
            className="cca-textarea cca-signoff-fixed-input"
            rows={2}
            value={draft.signOff?.fixed || ""}
            placeholder="Beste,&#10;{{firstName}}"
            onChange={(e) =>
              patch({
                signOff: { ...draft.signOff, mode: "fixed", fixed: e.target.value },
              })
            }
          />
        )}

        {signOffMode === "auto" && detectedClosings.length > 0 && (
          <div className="cca-signoff-detected">
            <span className="cca-signoff-detected-label">
              <i className="fa-solid fa-fingerprint" />
              Detected from your samples
            </span>
            <div className="cca-signoff-detected-list">
              {detectedClosings.slice(0, 4).map((c, i) => {
                const text = typeof c === "string" ? c : c.example || c.pattern;
                return (
                  <span key={i} className="cca-signoff-detected-pill">
                    {text}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {signOffMode === "auto" && detectedClosings.length === 0 && (
          <div className="cca-signoff-pending">
            <i className="fa-solid fa-circle-info" />
            Add sample emails and train the CopyTwin. The sign-off pattern will
            appear here automatically.
          </div>
        )}
      </div>

      {/* LANGUAGE — which language this clone writes in */}
      <div className="cca-guard">
        <div className="cca-guard-header">
          <i className="fa-solid fa-globe" style={{ color: "#6da7ed" }} />
          <div>
            <div className="cca-guard-title">Language</div>
            <div className="cca-guard-hint">
              The language this CopyTwin writes in. Auto-detect picks it up from
              your sample emails.
            </div>
          </div>
        </div>

        <div className="cca-lang-grid">
          {LANGUAGES.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`cca-lang-btn ${language === l.id ? "active" : ""}`}
              onClick={() => patch({ language: l.id })}
            >
              <i className={`fa-solid ${l.icon}`} />
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* STYLE TABOOS — phrases / patterns the AI must never use */}
      <div className="cca-guard">
        <div className="cca-guard-header">
          <i className="fa-solid fa-ban" style={{ color: "#ef4444" }} />
          <div>
            <div className="cca-guard-title">Style taboos</div>
            <div className="cca-guard-hint">
              Phrases, words or patterns the CopyTwin must <strong>never</strong> produce,
              regardless of campaign or context.
            </div>
          </div>
        </div>

        {availableSuggestions.length > 0 && (
          <div className="cca-taboo-suggestions">
            <span className="cca-taboo-suggestions-label">
              <i className="fa-solid fa-bolt" />
              Common AI tells, click to add
            </span>
            <div className="cca-taboo-suggestions-list">
              {availableSuggestions.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className="cca-taboo-suggestion"
                  onClick={() => addTaboo(s.value)}
                  title={`Add "${s.value}"`}
                >
                  <i className="fa-solid fa-plus" />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <ChipList
          items={taboos}
          accent="taboo"
          placeholder='Add your own, e.g. "best regards", em dashes, "synergy"'
          onAdd={(v) => addTaboo(v)}
          onRemove={(idx) =>
            patch({ taboos: taboos.filter((_, i) => i !== idx) })
          }
        />
      </div>
    </div>
  );
};

// ---------------- RIGHT PANEL — LIVE PREVIEW ----------------

const TrainingTierBar = ({ label, count, tiers, proTarget, icon }) => {
  const { current, next } = getTier(count, tiers);
  const hint = tierHint(count, tiers, proTarget);
  // Bar maxes out at Pro target; Overkill keeps it full.
  const pct = Math.min(100, (count / proTarget) * 100);
  const isOverkill = current.name === "Overkill";
  const isSweetSpot = current.name === "Solid" || current.name === "Pro";

  return (
    <div className="cca-tier" data-tier={current.name.toLowerCase()}>
      <div className="cca-tier-head">
        <span className="cca-tier-label">
          <i className={`fa-solid ${icon}`} />
          {label}
        </span>
        <span className="cca-tier-meta">
          <span className="cca-tier-count">
            {count} / {proTarget}
          </span>
          <span
            className={`cca-tier-badge ${isSweetSpot ? "sweet" : ""}`}
            style={{ color: current.color, borderColor: `${current.color}33` }}
          >
            {isSweetSpot && <i className="fa-solid fa-star" />}
            {current.name}
          </span>
        </span>
      </div>
      <div className="cca-tier-track">
        <div
          className="cca-tier-fill"
          style={{ width: `${pct}%`, background: current.color }}
        />
        {!isOverkill &&
          tiers.slice(1, 4).map((t) => {
            const markerPct = Math.min(100, (t.min / proTarget) * 100);
            return (
              <span
                key={t.name}
                className="cca-tier-marker"
                style={{ left: `${markerPct}%` }}
                title={`${t.name} · ${t.min}+`}
              />
            );
          })}
      </div>
      {hint && (
        <div className={`cca-tier-hint ${isOverkill ? "warn" : ""}`}>
          {hint}
        </div>
      )}
    </div>
  );
};

const LivePreview = ({
  clone,
  score,
  onTrain,
  isTraining,
  trainError,
  nextSteps = [],
  onNavigate,
}) => {
  const meta = confidenceLabel(score);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const hasIdentity = !!(clone.name && clone.name.trim());
  const emailCount = clone.voiceSamples?.fullEmails?.length || 0;
  const subjectCount = clone.voiceSamples?.subjectLines?.length || 0;
  const sampleCount = emailCount + subjectCount;
  const canTrain = sampleCount >= MIN_SAMPLES_FOR_TRAINING && !isTraining;
  const isStale =
    clone.voiceDNA &&
    sampleCount > 0 &&
    clone.voiceDNA.sampleCount > 0 &&
    sampleCount !== clone.voiceDNA.sampleCount;
  // DNA was distilled under an older engine version → it's missing the
  // newer style blocks (phrase bank, word DNA, voice context, …) that make
  // generated emails actually sound like the samples. Re-training repopulates
  // them. We only nudge when the samples themselves haven't changed (that
  // case already shows the stronger "samples changed" warning above).
  const isDnaOutdated = isCloneDnaOutdated(clone) && !isStale;

  return (
    <div className="cca-preview cca-neural cca-preview--wide">
      <div className="cca-preview-header">
        <div className="cca-preview-pulse" style={{ background: meta.color }} />
        <span>Neural Training Core</span>
      </div>

      <div className="cca-preview-wide-grid">
        <div className="cca-preview-core">
          {/* `key` flips with the lattice state so the SVG remounts fresh when
              training finishes. Without it the browser keeps the graphic on a
              stale composited layer and it renders blurry until a manual
              refresh re-rasterizes it. */}
          <NeuralCore
            key={isTraining ? "training" : clone.voiceDNA ? "online" : "dormant"}
            dna={clone.voiceDNA}
            score={score}
            isTraining={isTraining}
            phase={clone.trainingProgress?.phase}
          />
        </div>

        <div className="cca-preview-side">
          <div className="cca-preview-tiers">
            <TrainingTierBar
              label="Emails"
              count={emailCount}
              tiers={EMAIL_TIERS}
              proTarget={EMAIL_PRO_TARGET}
              icon="fa-envelope-open-text"
            />
            <TrainingTierBar
              label="Subjects"
              count={subjectCount}
              tiers={SUBJECT_TIERS}
              proTarget={SUBJECT_PRO_TARGET}
              icon="fa-heading"
            />
          </div>

          <div className="cca-preview-train">
            <button
              type="button"
              className="cca-train-btn"
              onClick={onTrain}
              disabled={!canTrain}
            >
              {isTraining ? (
                <>
                  <span className="cca-spinner cca-spinner-sm" />
                  {(() => {
                    const tp = clone.trainingProgress;
                    if (!tp || !tp.phase) return "Training your CopyTwin…";
                    if (tp.phase === "annotating") {
                      const total = tp.totalToAnnotate || 0;
                      const done = tp.annotatedCount || 0;
                      return total
                        ? `Annotating emails ${done}/${total}…`
                        : "Annotating emails…";
                    }
                    if (tp.phase === "aggregating") return "Synthesizing voice DNA…";
                    return "Training your CopyTwin…";
                  })()}
                </>
              ) : clone.voiceDNA ? (
                <>
                  <i className="fa-solid fa-rotate" />
                  Re-train CopyTwin
                </>
              ) : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles" />
                  Train CopyTwin
                </>
              )}
            </button>
            {!canTrain && !isTraining && (
              <span className="cca-train-hint">
                Add at least {MIN_SAMPLES_FOR_TRAINING} samples to start training.
              </span>
            )}
            {isStale && !isTraining && (
              <span className="cca-train-hint cca-train-hint-warn">
                <i className="fa-solid fa-triangle-exclamation" />
                Samples changed since last training. Re-train for best results.
              </span>
            )}
            {isDnaOutdated && !isTraining && (
              <span className="cca-train-hint cca-train-hint-warn">
                <i className="fa-solid fa-arrow-up-right-dots" />
                New voice intelligence available. Re-train to unlock the latest
                style engine (phrase bank, word DNA, persuasion triggers) so emails
                land closer to your samples.
              </span>
            )}
            {trainError && (
              <span className="cca-train-hint cca-train-hint-error">
                <i className="fa-solid fa-circle-exclamation" />
                {trainError}
              </span>
            )}
          </div>

          {/* Action buttons — what to do next, right where the eye lands. */}
          {onNavigate && (
            <div className="cca-preview-next">
              {nextSteps.length > 0 ? (
                <>
                  <span className="cca-preview-next-label">
                    <i className="fa-solid fa-bolt" />
                    Do this next
                  </span>
                  <div className="cca-preview-actions">
                    {nextSteps.slice(0, 3).map((step) => (
                      <button
                        key={step.id}
                        type="button"
                        className="cca-preview-action"
                        onClick={() => onNavigate(step.id)}
                      >
                        <span className="cca-preview-action-icon">
                          <i className={`fa-solid ${step.icon}`} />
                        </span>
                        <span className="cca-preview-action-text">
                          <span className="cca-preview-action-label">
                            {step.label}
                            {step.optional && (
                              <span className="cca-preview-action-opt">Optional</span>
                            )}
                          </span>
                          <span className="cca-preview-action-hint">{step.hint}</span>
                        </span>
                        <i className="fa-solid fa-arrow-right cca-preview-action-arrow" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="cca-preview-allset">
                  <span className="cca-preview-allset-icon">
                    <i className="fa-solid fa-circle-check" />
                  </span>
                  <span className="cca-preview-allset-text">
                    <span className="cca-preview-allset-title">You're all set</span>
                    <span className="cca-preview-allset-hint">
                      Every step is done. Re-run any round anytime to keep refining.
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className="cca-preview-summary-toggle"
        aria-expanded={summaryOpen}
        onClick={() => setSummaryOpen((v) => !v)}
      >
        <span>{summaryOpen ? "Show less" : "Show more"}</span>
        <i
          className={`fas fa-chevron-down cca-preview-summary-chevron ${
            summaryOpen ? "cca-preview-summary-chevron--open" : ""
          }`}
        />
      </button>

      {summaryOpen && (
        <div className="cca-preview-card cca-preview-summary">
          <div className="cca-preview-card-row">
            <span className="cca-preview-label">Voice confidence</span>
            <span className="cca-preview-value" style={{ color: meta.color }}>
              {meta.label} · {score}%
            </span>
          </div>
          <div className="cca-preview-bar">
            <div
              className="cca-preview-bar-fill"
              style={{ width: `${score}%`, background: meta.color }}
            />
          </div>

          <div className="cca-preview-checklist">
            <div className={`cca-preview-check ${hasIdentity ? "done" : ""}`}>
              <i className={`fa-solid ${hasIdentity ? "fa-circle-check" : "fa-circle"}`} />
              Profile labeled
            </div>
            <div
              className={`cca-preview-check ${
                (clone.taboos || []).length > 0 ? "done" : ""
              }`}
            >
              <i
                className={`fa-solid ${
                  (clone.taboos || []).length > 0 ? "fa-circle-check" : "fa-circle"
                }`}
              />
              Style taboos set (optional)
            </div>
            <div className={`cca-preview-check ${clone.voiceDNA ? "done" : ""}`}>
              <i
                className={`fa-solid ${
                  clone.voiceDNA ? "fa-circle-check" : "fa-circle"
                }`}
              />
              Voice DNA distilled
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------- EDITOR ROOT ----------------

const CloneEditor = ({ userId, cloneId, onBack }) => {
  const { clone, loading } = useCopyClone(userId, cloneId);
  const [view, setView] = useState("setup"); // "setup" (input/training) | "profile" (result)
  const [draft, setDraft] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  // Active Setup sub-navigation item. Starts on the dashboard (the CopyTwin
  // home base) so the user lands on the overview with their open to-dos.
  const [subTab, setSubTab] = useState("dashboard");

  const { distill, isPending, error: trainError } = useDistillation(userId);

  // Cloud Function crashes leave the DB stuck on status='training'.
  // The backend writes a heartbeat to `updatedAt` every 30s while a
  // training is alive, so any clone with `status='training'` and an
  // `updatedAt` older than 90s is definitively orphaned (server crashed
  // mid-run). Using updatedAt — not the original training start — means
  // long but healthy runs are never mis-detected as stale.
  const STALE_TRAINING_MS = 90 * 1000;
  const cloneStatus = clone?.status;
  const cloneUpdatedAt = clone?.updatedAt || 0;

  useEffect(() => {
    if (cloneStatus !== "training") return;
    if (!cloneUpdatedAt) return;
    const ageMs = Date.now() - cloneUpdatedAt;
    if (ageMs <= STALE_TRAINING_MS) return;
    if (isPending(cloneId)) return;
    if (!userId || !cloneId) return;
    console.warn("[CloneEditor] Stale training detected — resetting status");
    updateClone(userId, cloneId, { status: "draft" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneStatus, cloneUpdatedAt, userId, cloneId]);

  const isTraining = isPending(cloneId) || cloneStatus === "training";

  // Hydrate draft from RTDB on first load (and on cloneId switch).
  // After hydration, only reflect server-side changes coming from the
  // distillation engine (voiceDNA, subjectDNA, per-email annotations,
  // status, lastTrainedAt). User-editable fields are not overwritten.
  useEffect(() => {
    if (!clone) return;
    if (!draft || draft.id !== clone.id) {
      setDraft(clone);
      setIsDirty(false);
      return;
    }
    // Normalize both sides to the SAME shape so the comparison is stable.
    // Without this, undefined (from RTDB missing key) vs null (after our
    // coercion) would forever flip serverChanged to true → infinite loop.
    const incomingVoiceDNA      = clone.voiceDNA      ?? null;
    const incomingSubjectDNA    = clone.subjectDNA    ?? null;
    const incomingStatus        = clone.status        ?? "draft";
    const incomingLastTrainedAt = clone.lastTrainedAt ?? 0;
    const incomingProgress      = clone.trainingProgress ?? null;

    const draftVoiceDNA      = draft.voiceDNA      ?? null;
    const draftSubjectDNA    = draft.subjectDNA    ?? null;
    const draftStatus        = draft.status        ?? "draft";
    const draftLastTrainedAt = draft.lastTrainedAt ?? 0;
    const draftProgress      = draft.trainingProgress ?? null;

    // Compare progress by content (each RTDB snapshot is a fresh object, so a
    // reference check would loop forever). A stable key keeps the effect quiet
    // once the draft already mirrors the live phase/counts.
    const progressKey = (p) =>
      p ? `${p.phase}:${p.annotatedCount ?? 0}:${p.totalToAnnotate ?? 0}` : "none";
    const progressChanged = progressKey(incomingProgress) !== progressKey(draftProgress);

    // Calibration / subjectCalibration are written out-of-band by the
    // CalibrationDeck. Mirror the live values into the draft (read-only, for the
    // sub-nav "calibrated" checkmarks) — the auto-save deliberately omits these
    // keys, so syncing them here can never round-trip a stale copy back to RTDB.
    // Compared by a light content signature to keep the effect from looping.
    const calibKey = (c) =>
      c ? `${c.totalAttempts ?? 0}:${c.totalApproved ?? 0}:${(c.hardRejects || []).length}:${c.preferenceProfile ? 1 : 0}` : "none";
    const incomingCalibration        = clone.calibration        ?? null;
    const incomingSubjectCalibration = clone.subjectCalibration ?? null;
    const calibrationChanged =
      calibKey(incomingCalibration) !== calibKey(draft.calibration ?? null) ||
      calibKey(incomingSubjectCalibration) !== calibKey(draft.subjectCalibration ?? null);

    const serverChanged =
      incomingVoiceDNA      !== draftVoiceDNA   ||
      incomingSubjectDNA    !== draftSubjectDNA ||
      incomingStatus        !== draftStatus     ||
      incomingLastTrainedAt !== draftLastTrainedAt ||
      progressChanged ||
      calibrationChanged;

    // Per-email annotations may have arrived. Merge them into the existing
    // draft emails by id (only annotation field, preserve user edits).
    const draftEmails = draft.voiceSamples?.fullEmails || [];
    const cloneEmails = clone.voiceSamples?.fullEmails || [];
    const annotationsChanged = draftEmails.some((d) => {
      const incoming = cloneEmails.find((c) => c.id === d.id);
      return incoming && incoming.annotation && incoming.annotation !== d.annotation;
    });

    if (serverChanged || annotationsChanged) {
      setDraft((prev) => ({
        ...prev,
        voiceDNA:            incomingVoiceDNA,
        subjectDNA:          incomingSubjectDNA,
        status:              incomingStatus,
        lastTrainedAt:       incomingLastTrainedAt,
        trainingProgress:    incomingProgress,
        calibration:         incomingCalibration,
        subjectCalibration:  incomingSubjectCalibration,
        voiceSamples: {
          ...prev.voiceSamples,
          fullEmails: (prev.voiceSamples?.fullEmails || []).map((d) => {
            const incoming = cloneEmails.find((c) => c.id === d.id);
            return incoming?.annotation
              ? { ...d, annotation: incoming.annotation }
              : d;
          }),
        },
      }));
    }
  }, [clone, draft]);

  const patch = (partial) => {
    setDraft((prev) => ({ ...prev, ...partial }));
    setIsDirty(true);
  };

  const markClean = useCallback(() => setIsDirty(false), []);
  useAutoSave(userId, cloneId, draft, isDirty, markClean);

  // Auto-name once: as soon as a clone has a distilled voice (so a topic can be
  // detected) but still carries no label, fill in a sensible "Mon DD (Topic)"
  // default. Stays fully editable in the title; guarded so it never overwrites
  // a user-set name or re-fires on every render.
  const autoNamedRef = useRef(false);
  useEffect(() => {
    if (autoNamedRef.current) return;
    if (!draft) return;
    if (draft.name && draft.name.trim()) return;
    if (!draft.voiceDNA) return;
    autoNamedRef.current = true;
    patch({ name: autoCloneName(draft) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.voiceDNA, draft?.name]);

  const score = useMemo(
    () => (draft ? calculateVoiceConfidence(draft) : 0),
    [draft]
  );

  const sampleCount =
    (draft?.voiceSamples?.fullEmails?.length || 0) +
    (draft?.voiceSamples?.subjectLines?.length || 0);

  // Stale = there's a trained DNA but the sample set has changed since
  // it was distilled. Same logic LivePreview uses internally — pulled up
  // to the editor scope so the topbar status badge can show it too.
  const isStale = !!(
    draft?.voiceDNA &&
    sampleCount > 0 &&
    draft.voiceDNA.sampleCount > 0 &&
    sampleCount !== draft.voiceDNA.sampleCount
  );

  // Auto-train trigger removed — training only fires when the user
  // explicitly clicks "Train Clone" / "Re-train". Auto-firing on
  // sample changes burned credits silently and re-triggered every
  // time the editor remounted (sample count diff vs. fresh ref).

  const handleTrain = () => {
    if (sampleCount < MIN_SAMPLES_FOR_TRAINING) return;
    distill(cloneId);
  };

  if (loading || !draft) {
    return (
      <div className="cca-loading">
        <div className="cca-spinner" />
        <span>Loading CopyTwin…</span>
      </div>
    );
  }

  // Per-step completion — drives the sub-nav checkmarks and the dashboard
  // to-do list so the user always sees what is done and what is still open.
  const trained = !!draft.voiceDNA;
  const stepDone = {
    samples: trained,
    calibration: calibrationCoverage(draft.calibration).calibrated > 0,
    subjectcalib: subjectCalibrationCoverage(draft.subjectCalibration).calibrated > 0,
    profile: trained,
    guardrails: (draft.taboos || []).length > 0,
  };
  const isLocked = (item) => item.needsDna && !trained;
  // Open tasks shown on the dashboard: not done, actionable now (unlocked),
  // and optional steps are only suggested once the clone is trained.
  const openSteps = SUBNAV.filter(
    (item) =>
      item.id !== "dashboard" &&
      !stepDone[item.id] &&
      !isLocked(item) &&
      !(item.optional && !trained)
  );

  return (
    <div className="cca-editor">
      <div className="cca-editor-topbar">
        <button type="button" className="cca-back-btn" onClick={onBack}>
          <i className="fa-solid fa-arrow-left" />
          All twins
        </button>
        <div className="cca-editor-title-wrap">
          <input
            type="text"
            className="cca-editor-title-input"
            value={draft.name || ""}
            placeholder={autoCloneName(draft)}
            onChange={(e) => patch({ name: e.target.value })}
            aria-label="Profile label"
          />
          <i className="fa-solid fa-pen cca-editor-title-pen" aria-hidden="true" />
        </div>

        {/* Training status — at-a-glance badge so the user always knows
            whether this clone is ready to write or still needs training.
            Mirrors the same state machine as the LivePreview button:
              training → spinner + "Training…"
              ready    → green check + "Ready"
              stale    → amber + "Re-train recommended"
              draft    → grey + "Not trained"  */}
        <div
          className={`cca-editor-trainstate cca-editor-trainstate-${
            isTraining ? "training" :
            !draft.voiceDNA ? "untrained" :
            isStale ? "stale" : "ready"
          }`}
          title={
            isTraining ? "Distilling the voice DNA right now" :
            !draft.voiceDNA ? `Add ${Math.max(0, MIN_SAMPLES_FOR_TRAINING - sampleCount)} more samples and click Train CopyTwin` :
            isStale ? "Samples changed, re-train for best results" :
            "Voice DNA distilled and ready to generate emails"
          }
        >
          {isTraining ? (
            <>
              <span className="cca-spinner cca-spinner-sm" />
              <span>Training…</span>
            </>
          ) : !draft.voiceDNA ? (
            <>
              <i className="fa-solid fa-circle-dashed" />
              <span>Not trained</span>
            </>
          ) : isStale ? (
            <>
              <i className="fa-solid fa-triangle-exclamation" />
              <span>Re-train recommended</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-circle-check" />
              <span>Ready</span>
            </>
          )}
        </div>

        <div className="cca-editor-savestate">
          {isDirty ? (
            <>
              <span className="cca-savedot saving" />
              Saving…
            </>
          ) : (
            <>
              <span className="cca-savedot saved" />
              Synced
            </>
          )}
        </div>
      </div>

      {/* View switch — the two areas are fully separated: the user is either
          in the input/training area ("Setup") OR looking at the distilled
          result ("Voice Profile"). Never stacked. */}
      <div className="cca-viewseg" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "setup"}
          className={`cca-viewseg-btn ${view === "setup" ? "is-active" : ""}`}
          onClick={() => setView("setup")}
        >
          <i className="fa-solid fa-sliders" />
          Setup &amp; Training
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "profile"}
          className={`cca-viewseg-btn ${view === "profile" ? "is-active" : ""}`}
          onClick={() => setView("profile")}
        >
          <i className="fa-solid fa-fingerprint" />
          Voice Profile
          {!draft.voiceDNA && (
            <span className="cca-viewseg-lock"><i className="fa-solid fa-lock" /></span>
          )}
        </button>
      </div>

      {view === "setup" && (
      <div className="cca-setup-stack">
        {/* Sub-navigation — dashboard first, then the workflow steps. A green
            check marks every step that is already done. */}
        <div className="cca-subnav" role="tablist">
          {SUBNAV.map((item) => {
            const locked = isLocked(item);
            const done = item.id !== "dashboard" && !locked && stepDone[item.id];
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={subTab === item.id}
                className={`cca-subnav-btn ${subTab === item.id ? "is-active" : ""} ${
                  locked ? "is-locked" : ""
                } ${done ? "is-done" : ""}`}
                onClick={() => setSubTab(item.id)}
              >
                <span className="cca-subnav-icon">
                  <i className={`fa-solid ${locked ? "fa-lock" : item.icon}`} />
                  {done && (
                    <span className="cca-subnav-check">
                      <i className="fa-solid fa-check" />
                    </span>
                  )}
                </span>
                <span className="cca-subnav-text">
                  <span className="cca-subnav-label">{item.label}</span>
                  <span className="cca-subnav-hint">{item.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {subTab === "dashboard" ? (
          <>
            {/* Neural Training Core — the CopyTwin command center. The open
                to-dos live inside it as action buttons (see nextSteps). */}
            <LivePreview
              clone={draft}
              score={score}
              onTrain={handleTrain}
              isTraining={isTraining}
              trainError={trainError}
              nextSteps={openSteps}
              onNavigate={setSubTab}
            />
          </>
        ) : (
          <div className="cca-subnav-panel">
            {subTab === "samples" && (
              <>
                <EmailLibrarySection draft={draft} patch={patch} userId={userId} />
                <SubjectLinesSection draft={draft} patch={patch} />
              </>
            )}
            {subTab === "calibration" && (
              <CalibrationDeck userId={userId} cloneId={cloneId} clone={clone} />
            )}
            {subTab === "subjectcalib" && (
              <SubjectCalibrationDeck userId={userId} cloneId={cloneId} clone={clone} />
            )}
            {subTab === "profile" && (
              <IdentitySection draft={draft} patch={patch} />
            )}
            {subTab === "guardrails" && (
              <GuardrailsSection draft={draft} patch={patch} />
            )}
          </div>
        )}
      </div>
      )}

      {/* === Voice Profile — its own dedicated stage, fully separate from the
          Setup / Training area above. Apple-style result view. === */}
      {view === "profile" && (
      <section className="cca-vp-stage">
        <VoiceDnaPreview
          dna={draft.voiceDNA}
          cloneName={draft.name}
          confidenceScore={score}
          sampleCount={
            (draft.voiceSamples?.fullEmails?.length || 0) +
            (draft.voiceSamples?.subjectLines?.length || 0)
          }
          minSamples={MIN_SAMPLES_FOR_TRAINING}
          onRetrain={draft.voiceDNA ? handleTrain : undefined}
          isTraining={isTraining}
        />
      </section>
      )}
    </div>
  );
};

export default CloneEditor;
